/**
 * 정산 월별 상세 패널
 *
 *  정산 목록(마스터-디테일) 우측 패널과 /settlement/:id/:yearMonth 단독 페이지에서 공용.
 *  상단 요약 + 결제 내역(2줄 행) + 미정산 건 일괄 정산처리.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import {
  settlementAmountApi,
  type SettlementMonthDetailItem,
} from '@/services/api/settlementAmount';
import { exportToExcel, type ExcelColumn } from '@/utils/excel';
import { Toast } from '@/components/ui/Toast';
import {
  StatusBadge,
  LoadingState,
  ErrorState,
  EmptyState,
  type BadgeTone,
} from '@/components/common';

const STATUS_LABEL: Record<string, string> = {
  COMPLETED: '완료',
  PENDING: '대기',
  FAILED: '실패',
  CANCELED: '취소',
  CANCELLED: '취소',
};

const STATUS_TONE: Record<string, BadgeTone> = {
  COMPLETED: 'success',
  PENDING: 'warning',
  FAILED: 'danger',
  CANCELED: 'neutral',
  CANCELLED: 'neutral',
};

/** 정산 상태 탭 */
type StatusTab = 'ALL' | 'PENDING' | 'COMPLETED';

const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: 'ALL', label: '전체' },
  { key: 'PENDING', label: '미정산' },
  { key: 'COMPLETED', label: '완료' },
];

const won = (n: number | null | undefined) => `${Number(n ?? 0).toLocaleString()}원`;
const rate = (n: number | null | undefined) => (n == null ? '—' : `${n.toFixed(2)}%`);

export interface SettlementDetailTarget {
  companySeq: number;
  yearMonth: string;
  id?: string;
  companyName?: string;
  businessNumber?: string;
}

interface SettlementDetailPanelProps {
  target: SettlementDetailTarget;
  /** 우측 패널로 쓸 때 닫기 버튼 노출 (단독 페이지에서는 생략) */
  onClose?: () => void;
  /** 정산처리 성공 시 목록 갱신 */
  onSettled?: () => void;
}

export function SettlementDetailPanel({
  target,
  onClose,
  onSettled,
}: SettlementDetailPanelProps) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<StatusTab>('ALL');
  const [toast, setToast] = useState<{
    show: boolean;
    type: 'success' | 'error';
    message: string;
  }>({ show: false, type: 'success', message: '' });

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['settlementMonthDetail', target.companySeq, target.id, target.yearMonth],
    queryFn: () =>
      settlementAmountApi.getMonthDetail({
        yearMonth: target.yearMonth,
        companySeq: target.companySeq,
        id: target.id || undefined,
        companyName: target.companyName,
        businessNumber: target.businessNumber,
      }),
    enabled: !!target.yearMonth && target.companySeq != null,
    retry: false,
    staleTime: 30 * 1000,
  });

  const rows = useMemo(() => data?.online ?? [], [data]);

  const totals = useMemo(() => {
    let sales = 0;
    let settlement = 0;
    let pendingCount = 0;
    let pendingAmount = 0;
    let completedCount = 0;
    for (const r of rows) {
      sales += r.salesAmount ?? 0;
      settlement += r.settlementAmount ?? 0;
      if (r.status === 'COMPLETED') completedCount++;
      else if (r.status === 'PENDING') {
        pendingCount++;
        pendingAmount += r.settlementAmount ?? 0;
      }
    }
    return {
      sales,
      settlement,
      fee: sales - settlement,
      pendingCount,
      pendingAmount,
      completedCount,
    };
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (tab === 'ALL') return rows;
    return rows.filter(r => r.status === tab);
  }, [rows, tab]);

  /** 정산처리 — API 가 배열을 받으므로 건별/일괄 모두 같은 경로로 처리한다 */
  const settleMutation = useMutation({
    mutationFn: (settlementSeqs: number[]) => settlementAmountApi.updateStatus(settlementSeqs),
    onSuccess: (_, settlementSeqs) => {
      queryClient.invalidateQueries({ queryKey: ['settlementMonthDetail'] });
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      onSettled?.();
      setToast({
        show: true,
        type: 'success',
        message: `${settlementSeqs.length}건 정산처리가 완료되었습니다.`,
      });
    },
    onError: () => {
      setToast({ show: true, type: 'error', message: '정산처리에 실패했습니다.' });
    },
  });

  const handleSettleOne = (row: SettlementMonthDetailItem) => {
    if (!row.settlementSeq) {
      setToast({ show: true, type: 'error', message: '정산 정보가 올바르지 않습니다.' });
      return;
    }
    if (!window.confirm('해당 건을 정산처리 하시겠습니까?')) return;
    settleMutation.mutate([row.settlementSeq]);
  };

  const handleSettleAll = () => {
    const seqs = rows
      .filter(r => r.status === 'PENDING' && r.settlementSeq)
      .map(r => r.settlementSeq);
    if (seqs.length === 0) return;
    if (
      !window.confirm(
        `미정산 ${seqs.length}건 (${won(totals.pendingAmount)}) 을 일괄 정산처리 하시겠습니까?\n` +
          `되돌릴 수 없습니다.`,
      )
    )
      return;
    settleMutation.mutate(seqs);
  };

  const excelColumns: ExcelColumn<SettlementMonthDetailItem>[] = [
    { header: '결제 SEQ', accessor: r => r.paymentSeq },
    {
      header: '결제일',
      accessor: r => (r.dtRegist ? new Date(r.dtRegist).toLocaleString('ko-KR') : '—'),
    },
    { header: '회원명', accessor: r => r.userName ?? '' },
    { header: '이메일', accessor: r => r.userEmail ?? '' },
    { header: '연락처', accessor: r => r.phoneNumber ?? '' },
    { header: '상호명', accessor: r => r.companyName ?? '' },
    { header: '상품명', accessor: r => r.ticketName ?? '' },
    { header: '분할 횟수', accessor: r => r.splitCount ?? 0 },
    { header: '매출액', accessor: r => r.salesAmount ?? 0 },
    { header: 'PG 수수료', accessor: r => r.pgFee ?? 0 },
    { header: 'PG 수수료율(%)', accessor: r => (r.pgFeeRate ?? 0).toFixed(2) },
    { header: '서비스 수수료', accessor: r => r.serviceFee ?? 0 },
    { header: '서비스 수수료율(%)', accessor: r => (r.serviceFeeRate ?? 0).toFixed(2) },
    { header: '정산금액', accessor: r => r.settlementAmount ?? 0 },
    { header: '상태', accessor: r => STATUS_LABEL[r.status] ?? r.status ?? '' },
  ];

  const handleDownload = () => {
    const companyPart = target.companyName ? `_${target.companyName}` : '';
    exportToExcel(
      `정산상세_${target.yearMonth || 'all'}${companyPart}`,
      '정산내역',
      excelColumns,
      rows,
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <LoadingState message="월별 상세 데이터를 불러오는 중..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <ErrorState error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  const tabCounts: Record<StatusTab, number> = {
    ALL: rows.length,
    PENDING: totals.pendingCount,
    COMPLETED: totals.completedCount,
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border-2 border-primary-200 bg-white">
      {toast.show && (
        <Toast
          type={toast.type}
          title={toast.message}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
        />
      )}

      {/* ── 헤더 ── */}
      <div className="shrink-0 border-b border-gray-200 px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-base font-bold text-gray-900">
              {target.companyName ?? `사업장 #${target.companySeq}`}
              <span className="ml-2 text-xs font-normal text-gray-500">{target.yearMonth}</span>
            </h2>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {target.id ? `${target.id} · ` : ''}
              {target.businessNumber ?? ''} · 결제 {rows.length}건
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {totals.pendingCount > 0 && (
              <button
                type="button"
                onClick={handleSettleAll}
                disabled={settleMutation.isPending}
                className="inline-flex items-center gap-1 rounded-md bg-primary-500 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                미정산 {totals.pendingCount}건 일괄 정산
              </button>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={rows.length === 0}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download size={12} />
              엑셀
            </button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-gray-900"
              >
                닫기 ×
              </button>
            )}
          </div>
        </div>

        {/* 금액 요약 — 매출에서 수수료를 빼면 정산액이 되는 흐름을 한 줄로 */}
        <div className="mt-3 grid grid-cols-4 gap-2 rounded-md bg-gray-50 px-3 py-2">
          <SummaryCell label="매출 합계" value={won(totals.sales)} />
          <SummaryCell label="수수료 합계" value={`-${won(totals.fee)}`} />
          <SummaryCell label="정산 합계" value={won(totals.settlement)} strong />
          <SummaryCell
            label="미정산 잔액"
            value={won(totals.pendingAmount)}
            tone={totals.pendingAmount > 0 ? 'warning' : undefined}
          />
        </div>
      </div>

      {/* ── 상태 탭 ── */}
      <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-3">
        <div className="flex gap-1">
          {STATUS_TABS.map(t => {
            const active = tab === t.key;
            const count = tabCounts[t.key];
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`-mb-px shrink-0 border-b-2 px-2.5 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {t.label}
                {count > 0 && (
                  <span
                    className={`ml-1 text-xs tabular-nums ${
                      active ? 'text-primary-500' : 'text-gray-400'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 결제 내역 (내부 스크롤) ── */}
      <div
        className={`min-h-0 flex-1 overflow-y-auto bg-gray-50 p-4 ${
          isFetching ? 'opacity-70' : ''
        }`}
      >
        {visibleRows.length === 0 ? (
          <EmptyState message="해당 상태의 결제 내역이 없습니다." />
        ) : (
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
            {visibleRows.map(row => (
              <PaymentRow
                key={row.paymentSeq}
                row={row}
                onSettle={handleSettleOne}
                disabled={settleMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCell({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: 'warning';
}) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[10px] text-gray-500">{label}</div>
      <div
        className={`mt-0.5 truncate text-sm tabular-nums ${
          tone === 'warning'
            ? 'font-semibold text-amber-700'
            : strong
              ? 'font-bold text-primary-600'
              : 'font-semibold text-gray-900'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

/** 결제 1건 — 2줄 압축 (회원/상품 / 매출→정산 + 수수료) */
function PaymentRow({
  row,
  onSettle,
  disabled,
}: {
  row: SettlementMonthDetailItem;
  onSettle: (row: SettlementMonthDetailItem) => void;
  disabled: boolean;
}) {
  const meta = [
    row.userName ? `${row.userName}${row.userEmail ? ` (${row.userEmail})` : ''}` : null,
    row.phoneNumber || null,
    row.ticketName || null,
    row.splitCount ? `${row.splitCount}회` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 text-sm font-semibold text-gray-900">
            {row.dtRegist ? new Date(row.dtRegist).toLocaleString('ko-KR') : '—'}
          </span>
          <StatusBadge tone={STATUS_TONE[row.status] ?? 'neutral'}>
            {STATUS_LABEL[row.status] ?? row.status}
          </StatusBadge>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-[11px] text-gray-500">
            {won(row.salesAmount)} →{' '}
            <span className="font-semibold text-primary-600">{won(row.settlementAmount)}</span>
          </span>
          {row.status !== 'COMPLETED' && (
            <button
              type="button"
              onClick={() => onSettle(row)}
              disabled={disabled}
              className="rounded-md border border-primary-300 bg-white px-2 py-0.5 text-[11px] font-medium text-primary-600 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
            >
              정산처리
            </button>
          )}
        </div>
      </div>
      <div className="mt-0.5 truncate text-[11px] text-gray-400" title={meta}>
        {meta}
        <span className="ml-1 text-gray-300">
          · PG {won(row.pgFee)} ({rate(row.pgFeeRate)}) · 서비스 {won(row.serviceFee)} (
          {rate(row.serviceFeeRate)})
        </span>
      </div>
    </div>
  );
}
